import { apiFetch } from "@/integrations/backend/client";

export type LessonRow = {
  id: string;
  title: string;
  motivations: string[];
  short_description: string;
  article: string;
  video_url: string | null;
  thumbnail_file: string | null;
  infographic_file: string | null;
  lesson_number: number;
  is_disabled: boolean;
  disabled_at: string | null;
  is_completed: boolean;
  completed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type LessonCompletionResponse = {
  lesson_id: string;
  is_completed: boolean;
  completed_at: string | null;
};

export type LessonCreateInput = {
  title: string;
  motivations: string[];
  short_description: string;
  article: string;
  video_url?: string | null;
  lesson_number?: number;
  is_disabled?: boolean;
};

export type LessonUpdateInput = Partial<LessonCreateInput> & {
  lesson_number?: number;
  is_disabled?: boolean;
  updated_at?: string;
};

export async function listLessons(includeDisabled: boolean = true): Promise<LessonRow[]> {
  const res = await apiFetch(`/api/v1/lessons?include_disabled=${includeDisabled ? "true" : "false"}`, { method: "GET" });
  if (!res.ok) throw new Error(`lessons_list_failed:${res.status}`);
  return (await res.json()) as LessonRow[];
}

export async function createLesson(
  input: LessonCreateInput,
  thumbnailImage?: File,
  infographicImage?: File
): Promise<LessonRow> {
  const form = new FormData();
  form.append("title", input.title);
  form.append("motivations", JSON.stringify(input.motivations ?? []));
  form.append("short_description", input.short_description);
  form.append("article", input.article);
  if (input.video_url) form.append("video_url", input.video_url);
  if (input.lesson_number !== undefined) form.append("lesson_number", String(input.lesson_number));
  form.append("is_disabled", String(input.is_disabled ?? false));
  if (thumbnailImage) {
    form.append("thumbnail_image", thumbnailImage);
  }
  if (infographicImage) {
    form.append("infographic_image", infographicImage);
  }
  const res = await apiFetch("/api/v1/lessons", {
    method: "POST",
    body: form,
  });
  if (!res.ok) throw new Error(`lesson_create_failed:${res.status}`);
  return (await res.json()) as LessonRow;
}

export async function updateLesson(
  id: string,
  updates: LessonUpdateInput,
  thumbnailImage?: File,
  infographicImage?: File
): Promise<LessonRow> {
  const form = new FormData();
  if (updates.title !== undefined) form.append("title", updates.title);
  if (updates.motivations !== undefined) form.append("motivations", JSON.stringify(updates.motivations ?? []));
  if (updates.short_description !== undefined) form.append("short_description", updates.short_description);
  if (updates.article !== undefined) form.append("article", updates.article);
  if (updates.video_url !== undefined) form.append("video_url", updates.video_url ?? "");
  if (updates.lesson_number !== undefined) form.append("lesson_number", String(updates.lesson_number));
  if (typeof updates.is_disabled === "boolean") {
    form.append("is_disabled", String(updates.is_disabled));
  }
  if (thumbnailImage) {
    form.append("thumbnail_image", thumbnailImage);
  }
  if (infographicImage) {
    form.append("infographic_image", infographicImage);
  }
  const res = await apiFetch(`/api/v1/lessons/${id}`, {
    method: "PATCH",
    body: form,
  });
  if (!res.ok) throw new Error(`lesson_update_failed:${res.status}`);
  return (await res.json()) as LessonRow;
}

export async function getLessonById(id: string): Promise<LessonRow> {
  const res = await apiFetch(`/api/v1/lessons/${id}`, { method: "GET" });
  if (!res.ok) throw new Error(`lesson_get_failed:${res.status}`);
  return (await res.json()) as LessonRow;
}

export async function completeLesson(id: string): Promise<LessonCompletionResponse> {
  const res = await apiFetch(`/api/v1/lessons/${id}/complete`, { method: "POST" });
  if (!res.ok) throw new Error(`lesson_complete_failed:${res.status}`);
  return (await res.json()) as LessonCompletionResponse;
}

export async function getFilesById(ids: string[]): Promise<Record<string, string>> {
  if (ids.length === 0) return {};
  const qs = encodeURIComponent(ids.join(","));
  const res = await apiFetch(`/api/v1/files/by-id?ids=${qs}`, { method: "GET" });
  if (!res.ok) throw new Error(`files_by_id_failed:${res.status}`);
  const arr = (await res.json()) as Array<{ id: string; path: string }>;
  const map: Record<string, string> = {};
  for (const it of arr) map[it.id] = it.path;
  return map;
}


